import {
    AfterViewInit,
    Component,
    DoCheck,
    EventEmitter,
    Input,
    IterableChanges,
    IterableDiffer,
    IterableDiffers,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';

import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

import { TwcSelectComponent, TwcSelectOptionComponent } from '@topcon/twc-angular';

import { MagnetService } from '@mirage-projects/cw-commons';
import { PcvizJobAdapter, PcvizJobAdapterFormatDefinition } from '@mirage-projects/cw-models';
import { JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { WizardFormatDefinition } from '../../../models/wizard-format-option';
import { UploadWizardService } from '../../../services/upload-wizard-service/upload-wizard.service';
import { InputFileComponent, InputFileDialogMode } from '../../input-file/input-file.component';
import { CreateWizardInputDataService } from './create-wizard-input-data.service';

/**
 * Import project/layer first panel, where the user can upload the required files to create the project/layer
 */
@Component({
    selector: 'cw-create-wizard-input-data',
    templateUrl: 'create-wizard-input-data.component.html',
    styleUrls: ['create-wizard-input-data.component.scss'],
    providers: [CreateWizardInputDataService],
    standalone: false,
})
export class CreateWizardInputDataComponent implements OnInit, DoCheck, AfterViewInit, OnDestroy {
    /** Text that will be shown as a definition of the wizard in all the steps */
    @Input() subtitle = '';

    /** Event to check if optional steps have to be shown when adding a file */
    @Output() optionalStep = new EventEmitter<PcvizJobAdapter>();

    @ViewChild('formatSelect') formatSelect!: TwcSelectComponent;
    @ViewChild(InputFileComponent) inputFile!: InputFileComponent;

    acceptedFiles = '';
    isVisibleCRS = true;
    formatDefinitions: BehaviorSubject<WizardFormatDefinition[]> | undefined;
    iterableDiffer: IterableDiffer<File> = this.iterableDiffers.find([]).create();
    isLoading = false;

    private onDestroy$: Subject<void> = new Subject();

    constructor(
        public uploadWizardService: UploadWizardService,
        private magnetService: MagnetService,
        public wizardInputservice: CreateWizardInputDataService,
        private iterableDiffers: IterableDiffers,
        private jobAdapterService: JobPcvizService
    ) {}

    ngOnInit(): void {
        this.formatDefinitions = this.uploadWizardService.formatsDefinition;
        this.uploadWizardService.filesLoaded$.pipe(takeUntil(this.onDestroy$)).subscribe(() => (this.isLoading = false));
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    ngDoCheck(): void {
        // TODO: Ask TWC to add an event to know when a file is removed
        // This is a litle hack to be able to know when a file is removed from the TWC upload list
        const changes: IterableChanges<File> | null = this.iterableDiffer.diff(this.uploadWizardService.files);

        if (changes && this.uploadWizardService.files.length === 0) {
            this.removedFiles();
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.isVisibleCRS = !this.magnetService.isSite() && this.uploadWizardService.importType === 'layer';
            this.acceptedFiles = this.wizardInputservice.acceptedFiles();
        });
        // [INTEGRATION] There is some race condition when is run in pointcloudviz, and options are not fully operative when the ngAfterViewInit is called
        setTimeout(() => {
            // ! [ANGULAR_UPGRADE] Remove any, check other way of getting options
            (this.formatSelect as any)?.options.forEach((option: TwcSelectOptionComponent) => {
                option.ngAfterViewInit();
            });
        }, 500);
    }

    onRemoveError(): void {
        this.uploadWizardService.pathError = undefined;
    }

    async onAddFile(): Promise<void> {
        await this.wizardInputservice.onFileChange(this.uploadWizardService.files);
        this.optionalStep.emit(this.jobAdapterService.getJobAdapter());
    }

    onChangeFormatSelected(e: PcvizJobAdapterFormatDefinition): void {
        this.uploadWizardService.files = [];
        this.wizardInputservice.formatChanged(e);
        if (this.isImportStation) {
            this.openDialog();
        }
    }

    getDialogMode(): InputFileDialogMode {
        if (this.isImportStation && !this.uploadWizardService.formatSelection) {
            return 'disabled';
        }
        if (this.isImportStation && this.isCrp1) {
            return 'external';
        }

        return 'internal';
    }

    isImageFormatSelected(): boolean {
        return this.uploadWizardService.formatSelection?.id?.startsWith('UAV') ?? false;
    }

    openExternalDialog(): void {
        this.uploadWizardService.openExternalDialog();
        this.isLoading = true;
    }

    get errorLayerFilesNotValid(): boolean {
        return this.uploadWizardService.importType === 'layer' && this.uploadWizardService.areFilesInvalid();
    }

    get formatNotFound(): boolean {
        return this.uploadWizardService.files.length > 0 && !this.jobAdapterService.getJobAdapter();
    }

    get formatNoticeString(): string {
        let result = `UPLOAD.FORMAT_NOTICE_${this.uploadWizardService.formatSelection?.label}`;

        // TODO: Remove when jobsite functionality is done
        if (this.uploadWizardService.jobSiteId && this.uploadWizardService.importType === 'project') {
            result += '_JOB_SITE';
        }

        return result;
    }

    get inputDataNoticeLabel(): string {
        return this.isImportStation ? 'UPLOAD.INSTRUMENT_DATA_NOTICE' : 'UPLOAD.SELECT_FORMAT_NOTICE';
    }

    get inputDataDropdownLabel(): string {
        return this.isImportStation ? 'UPLOAD.INSTRUMENT_DATA' : 'UPLOAD.SELECT_FORMAT';
    }

    get inputDataDropdownNotSelectedLabel(): string {
        return this.isImportStation ? 'UPLOAD.FORMAT_NOT_SELECTED_IMPORT_STATION' : 'UPLOAD.FORMAT_NOT_SELECTED';
    }

    get isImportStation(): boolean {
        return this.uploadWizardService.isImportStation;
    }

    get isAutoOpenEnabled(): boolean {
        return this.magnetService.isSite() && !this.isImportStation;
    }

    private removedFiles(): void {
        this.wizardInputservice.refreshFilesList(this.uploadWizardService.files);
    }

    private openDialog(): void {
        if (this.isCrp1) {
            this.openExternalDialog();
            return;
        }
        this.inputFile.twcInput.browse();
    }

    private get isCrp1(): boolean {
        return this.uploadWizardService.isCrp1;
    }
}
